import React, { useState, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import {
  buildTimeEntryImportTemplate,
  downloadTextFile,
  downloadTimeEntryImportTemplate,
  generateImportErrorsCsv,
  ImportTemplateData,
  MAX_IMPORT_ROWS,
  parseTimeEntryImportFile,
} from '../pages/time-entries/timeEntryImportTemplate';

function getImportErrorMessage(err: unknown, fallback: string): string {
  const apiMessage = (err as { response?: { data?: { message?: string } } })?.response?.data
    ?.message;
  if (apiMessage) {
    if (
      apiMessage.includes('maxItems') ||
      apiMessage.includes('must NOT have more than')
    ) {
      return `You can import at most ${MAX_IMPORT_ROWS} records at a time. Please split your file and try again.`;
    }
    return apiMessage;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

interface ImportValidationError {
  row: number;
  field: string;
  value?: string;
  message: string;
}

interface ImportSummary {
  total: number;
  valid: number;
  invalid: number;
  errors: ImportValidationError[];
  created?: number;
}

interface TimeEntryImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (created: number) => void;
}

type Step = 'upload' | 'summary';

export default function TimeEntryImportModal({
  isOpen,
  onClose,
  onSuccess,
}: TimeEntryImportModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [error, setError] = useState('');
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const resetState = () => {
    setStep('upload');
    setSelectedFile(null);
    setSummary(null);
    setError('');
    setIsDownloadingTemplate(false);
    setIsValidating(false);
    setIsImporting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Parent closes via onSuccess without calling handleClose — reset when modal hides.
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    setError('');
    try {
      const response = await api.get<ImportTemplateData>('/time-entries/import-template-data');
      const blob = await buildTimeEntryImportTemplate(response.data);
      downloadTimeEntryImportTemplate(blob);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to download template. Please try again.';
      setError(message);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setError('');
    setSummary(null);
    setStep('upload');
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    setIsValidating(true);
    setError('');
    try {
      const rows = await parseTimeEntryImportFile(selectedFile);
      if (rows.length === 0) {
        setError('No data rows found in the uploaded file.');
        return;
      }
      if (rows.length > MAX_IMPORT_ROWS) {
        setError(
          `You can import at most ${MAX_IMPORT_ROWS} records at a time. Your file has ${rows.length} records. Please split the file and try again.`
        );
        return;
      }

      const response = await api.post<ImportSummary>('/time-entries/import', {
        rows,
        dryRun: true,
      });

      setSummary(response.data);
      setStep('summary');
    } catch (err: unknown) {
      setError(getImportErrorMessage(err, 'Failed to validate the uploaded file.'));
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !summary || summary.valid === 0) return;

    setIsImporting(true);
    setError('');
    try {
      const rows = await parseTimeEntryImportFile(selectedFile);
      if (rows.length > MAX_IMPORT_ROWS) {
        setError(
          `You can import at most ${MAX_IMPORT_ROWS} records at a time. Your file has ${rows.length} records. Please split the file and try again.`
        );
        return;
      }

      const response = await api.post<ImportSummary>('/time-entries/import', {
        rows,
        dryRun: false,
      });

      setSummary(response.data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['timeEntries'] }),
        queryClient.invalidateQueries({ queryKey: ['timeEntriesHierarchy'] }),
        queryClient.invalidateQueries({ queryKey: ['timeEntryStats'] }),
      ]);

      if ((response.data.created ?? 0) > 0) {
        onSuccess?.(response.data.created ?? 0);
      } else if (response.data.valid > 0) {
        setError(
          'Validation passed but no entries were created. Please restart the backend server and try again.'
        );
      }
    } catch (err: unknown) {
      setError(getImportErrorMessage(err, 'Failed to import time entries.'));
    } finally {
      setIsImporting(false);
    }
  };

  const handleDownloadErrors = () => {
    if (!summary?.errors?.length) return;
    const csv = generateImportErrorsCsv(summary.errors);
    downloadTextFile(
      csv,
      `record-time-import-errors-${new Date().toISOString().slice(0, 10)}.csv`,
      'text/csv;charset=utf-8;'
    );
  };

  if (!isOpen) return null;

  const importComplete =
    (summary?.created ?? 0) > 0 && (summary?.errors.length ?? 0) === 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />
        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
              <ArrowUpTrayIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg font-semibold leading-6 text-gray-900">
                Import Record Time
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Download the latest template, fill in your time entries using the dropdowns, then upload the file for validation.
              </p>

              <div className="mt-4 space-y-4">
                <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
                  <p className="font-medium">Before you import</p>
                  <ul className="mt-1 list-disc pl-5 space-y-0.5">
                    <li>Download the latest template to get current dropdown values from the application.</li>
                    <li>Select in order: <strong>Customer</strong> → <strong>Engagement</strong> → <strong>Group</strong> and <strong>Type</strong>.</li>
                    <li>Engagement options appear only after Customer is selected. Group and Type appear only after Engagement is selected.</li>
                    <li>Columns: Worked Date, Customer, Engagement, Group, Type, Time Spent (In Mins), Task (optional), Description.</li>
                    <li>Worked Date: type DD/MM/YYYY manually (e.g. 03/09/2026) or use the calendar picker in column A.</li>
                    <li>You can import at most <strong>{MAX_IMPORT_ROWS} records</strong> per file.</li>
                  </ul>
                </div>

                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  disabled={isDownloadingTemplate}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {isDownloadingTemplate ? 'Downloading...' : 'Download Latest Template'}
                </button>

                {step === 'upload' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload completed template
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                    {selectedFile && (
                      <p className="mt-1 text-xs text-gray-500">Selected: {selectedFile.name}</p>
                    )}
                  </div>
                )}

                {step === 'summary' && summary && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-lg border border-gray-200 p-3 text-center">
                        <p className="text-xs text-gray-500">Total Records</p>
                        <p className="text-xl font-semibold text-gray-900">{summary.total}</p>
                      </div>
                      <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center">
                        <p className="text-xs text-green-700">Valid Records</p>
                        <p className="text-xl font-semibold text-green-800">{summary.valid}</p>
                      </div>
                      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                        <p className="text-xs text-red-700">Records with Errors</p>
                        <p className="text-xl font-semibold text-red-800">{summary.invalid}</p>
                      </div>
                    </div>

                    {importComplete && (
                      <div className="flex items-start gap-2 rounded-md bg-green-50 p-3 text-sm text-green-800">
                        <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <span>Successfully imported {summary.created} time {summary.created === 1 ? 'entry' : 'entries'}.</span>
                      </div>
                    )}

                    {summary.errors.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-gray-700 flex items-center gap-1">
                            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                            Validation errors
                          </p>
                          <button
                            type="button"
                            onClick={handleDownloadErrors}
                            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            Download error details
                          </button>
                        </div>
                        <div className="max-h-48 overflow-y-auto rounded-md border border-gray-200">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">Row</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">Field</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-500">Error</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                              {summary.errors.map((err, idx) => (
                                <tr key={`${err.row}-${err.field}-${idx}`}>
                                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">{err.row}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">{err.field}</td>
                                  <td className="px-3 py-2 text-gray-700">
                                    {err.value ? (
                                      <>
                                        <span className="text-gray-500">{err.value}</span>
                                        {' — '}
                                      </>
                                    ) : null}
                                    {err.message}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          Correct the errors in your Excel file and upload again. Only valid records will be imported.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {error && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
            {step === 'upload' && (
              <button
                type="button"
                onClick={handleValidate}
                disabled={!selectedFile || isValidating}
                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:w-auto disabled:opacity-50"
              >
                {isValidating ? 'Validating...' : 'Validate & Preview'}
              </button>
            )}
            {step === 'summary' && summary && summary.valid > 0 && !importComplete && (
              <button
                type="button"
                onClick={handleImport}
                disabled={isImporting}
                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:w-auto disabled:opacity-50"
              >
                {isImporting ? 'Importing...' : `Import ${summary.valid} Valid Record${summary.valid === 1 ? '' : 's'}`}
              </button>
            )}
            {step === 'summary' && (
              <button
                type="button"
                onClick={() => {
                  setStep('upload');
                  setSummary(null);
                  setSelectedFile(null);
                  setError('');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
              >
                {importComplete ? 'Import Another File' : 'Upload Another File'}
              </button>
            )}
            <button
              type="button"
              onClick={handleClose}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            >
              {importComplete ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
